import React from 'react';
import { T } from './orders.theme';
import { COPY } from './orders.copy';
import { CloudOffIcon, ReceiptIcon } from './OrdersIcons';

/**
 * What the screen shows before there is a list. All three are real states the
 * old screen rendered as a blank page.
 */

const SHIMMER_KEYFRAMES = `
@keyframes ordersShimmer {
  0%   { background-position: -420px 0; }
  100% { background-position: 420px 0; }
}`;

const block = (height, radius = T.radiusControl) => ({
  height,
  borderRadius: radius,
  background: `linear-gradient(90deg, #ECEEF1 0%, #F6F7F9 50%, #ECEEF1 100%)`,
  backgroundSize: '840px 100%',
  animation: 'ordersShimmer 1.4s ease-in-out infinite',
});

/** Shaped like the real layout, so nothing jumps when the data lands. */
export function OrdersSkeleton({ showSummary }) {
  return (
    <>
      <style>{SHIMMER_KEYFRAMES}</style>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...block(showSummary ? 86 : 20, T.radiusPanel), flexShrink: 0 }} />

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {[64, 82, 64, 76].map((width, i) => (
            <div key={i} style={{ ...block(T.controlHeight), width }} />
          ))}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusPanel,
            padding: '12px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 2px 2px' }}>
            <div style={{ ...block(11, 4), width: 104 }} />
            <div style={{ ...block(11, 4), width: 78 }} />
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={block(T.rowHeight)} />
          ))}
        </div>
      </div>
    </>
  );
}

const Shell = ({ children }) => (
  <div
    style={{
      flex: 1,
      minHeight: 0,
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusPanel,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 13,
      padding: 32,
    }}
  >
    {children}
  </div>
);

const Halo = ({ tint, children }) => (
  <div
    style={{
      width: 58,
      height: 58,
      borderRadius: T.radiusPill,
      background: tint,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {children}
  </div>
);

const quietButton = {
  height: 34,
  padding: '0 15px',
  borderRadius: T.radiusControl,
  background: T.surface,
  border: `1px solid ${T.borderStrong}`,
  fontSize: 12.5,
  fontWeight: 600,
  color: T.ink,
  cursor: 'pointer',
  font: 'inherit',
};

/**
 * Names the filters that emptied the list and offers the way out, rather than
 * leaving someone to guess which control is hiding their orders.
 */
export function OrdersEmpty({ reason, canClearFilters, onClearFilters, onWiden }) {
  return (
    <Shell>
      <Halo tint={T.canvas}>
        <ReceiptIcon size={26} color={T.inkFaint} width={1.5} />
      </Halo>
      <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{COPY.emptyTitle}</span>
      <span
        style={{
          fontSize: 12.5,
          color: T.inkSoft,
          textAlign: 'center',
          lineHeight: 1.5,
          maxWidth: 320,
        }}
      >
        {reason}
      </span>
      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        {canClearFilters && (
          <button type="button" onClick={onClearFilters} style={quietButton}>
            {COPY.clearFilters}
          </button>
        )}
        <button type="button" onClick={onWiden} style={quietButton}>
          {COPY.widenRange}
        </button>
      </div>
    </Shell>
  );
}

export function OrdersError({ message, onRetry }) {
  return (
    <Shell>
      <Halo tint={T.dangerTint}>
        <CloudOffIcon size={26} color={T.danger} width={1.5} />
      </Halo>
      <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{COPY.errorTitle}</span>
      <span
        style={{
          fontSize: 12.5,
          color: T.inkSoft,
          textAlign: 'center',
          lineHeight: 1.5,
          maxWidth: 320,
        }}
      >
        {message || COPY.errorBody}
      </span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          height: 36,
          padding: '0 20px',
          borderRadius: T.radiusControl,
          background: T.brand,
          border: 'none',
          fontSize: 13,
          fontWeight: 700,
          color: T.surface,
          cursor: 'pointer',
          marginTop: 2,
          font: 'inherit',
        }}
      >
        {COPY.retry}
      </button>
    </Shell>
  );
}
